import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { spacing } from '../constants/spacing';
import { typography } from '../constants/typography';
import { useAppColors } from '../context/PreferencesContext';
import { AppButton } from './AppButton';
import { AppInput } from './AppInput';

export type SelectorOption = {
  id: number;
  label: string;
  subtitle?: string | null;
};

type Props = {
  label: string;
  selectedValue: number | null;
  selectedLabel: string | null;
  loadOptions: (search: string) => Promise<SelectorOption[]>;
  onSelect: (option: SelectorOption) => void;
  onClear: () => void;
  error?: string;
  disabled?: boolean;
  placeholder?: string;
  minSearchLength?: number;
  /** Shown when the lookup returns no rows (default: "No matches found."). */
  emptyMessage?: string;
};

const SEARCH_DEBOUNCE_MS = 400;

export function SearchableEntitySelector({
  label,
  selectedValue,
  selectedLabel,
  loadOptions,
  onSelect,
  onClear,
  error,
  disabled,
  placeholder = 'Search…',
  minSearchLength = 0,
  emptyMessage = 'No matches found.',
}: Props) {
  const palette = useAppColors();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState<SelectorOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const requiredLength = Math.max(0, minSearchLength);

  const requestId = useRef(0);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mounted = useRef(true);
  const loadOptionsRef = useRef(loadOptions);
  loadOptionsRef.current = loadOptions;

  const runSearch = useCallback(async (value: string) => {
    const trimmed = value.trim();
    if (trimmed.length < requiredLength) {
      requestId.current += 1;
      setLoading(false);
      setLookupError(null);
      setOptions([]);
      return;
    }

    const current = ++requestId.current;
    if (!mounted.current) return;
    setLoading(true);
    setLookupError(null);
    try {
      const result = await loadOptionsRef.current(trimmed);
      if (!mounted.current || current !== requestId.current) return;
      setOptions(result);
    } catch {
      if (mounted.current && current === requestId.current) {
        setOptions([]);
        setLookupError('Unable to load options.');
      }
    } finally {
      if (mounted.current && current === requestId.current) {
        setLoading(false);
      }
    }
  }, [requiredLength]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      requestId.current += 1;
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  function openPicker() {
    if (disabled) return;
    setOpen(true);
    setSearch('');
    setOptions([]);
    setLookupError(null);
    if (requiredLength === 0) {
      runSearch('');
    }
  }

  function closePicker() {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    setOpen(false);
    setSearch('');
    setOptions([]);
    setLookupError(null);
  }

  function onSearchChange(value: string) {
    setSearch(value);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    if (value.trim().length < requiredLength) {
      requestId.current += 1;
      setLoading(false);
      setLookupError(null);
      setOptions([]);
      return;
    }
    debounceTimer.current = setTimeout(() => {
      runSearch(value);
    }, SEARCH_DEBOUNCE_MS);
  }

  const displayValue = selectedLabel ?? 'None selected';

  return (
    <View style={styles.container}>
      <Text style={[styles.label, { color: palette.textSecondary }]}>
        {label}
      </Text>
      <View style={styles.selectedRow}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={label}
          accessibilityState={{ expanded: open, disabled: Boolean(disabled) }}
          disabled={disabled}
          onPress={open ? closePicker : openPicker}
          style={[
            styles.dropdownButton,
            {
              backgroundColor: palette.surface,
              borderColor: palette.border,
            },
            disabled && styles.disabled,
          ]}
        >
          <Text
            style={[
              styles.dropdownLabel,
              {
                color: selectedLabel
                  ? palette.textPrimary
                  : palette.textMuted,
              },
            ]}
            numberOfLines={1}
          >
            {displayValue}
          </Text>
          <Text style={{ color: palette.textMuted }}>{open ? '▴' : '▾'}</Text>
        </Pressable>
        {selectedValue != null && selectedLabel && !disabled ? (
          <AppButton title="Clear" variant="text" onPress={onClear} />
        ) : null}
      </View>

      {open && !disabled ? (
        <View
          style={[
            styles.panel,
            {
              borderColor: palette.border,
              backgroundColor: palette.surface,
            },
          ]}
        >
          <AppInput
            label="Search"
            value={search}
            onChangeText={onSearchChange}
            placeholder={placeholder}
            autoCapitalize="none"
          />
          {loading ? (
            <ActivityIndicator color={palette.primary} />
          ) : lookupError ? (
            <View>
              <Text style={[styles.error, { color: palette.error }]}>
                {lookupError}
              </Text>
              <AppButton
                title="Retry"
                variant="secondary"
                onPress={() => runSearch(search)}
              />
            </View>
          ) : search.trim().length < requiredLength ? (
            <Text style={[styles.help, { color: palette.textMuted }]}>
              {`Enter at least ${requiredLength} characters to search.`}
            </Text>
          ) : options.length === 0 ? (
            <Text style={[styles.help, { color: palette.textMuted }]}>
              {emptyMessage}
            </Text>
          ) : (
            options.map(option => (
              <Pressable
                key={option.id}
                accessibilityRole="button"
                accessibilityLabel={option.label}
                style={[styles.option, { borderBottomColor: palette.border }]}
                onPress={() => {
                  onSelect(option);
                  closePicker();
                }}
              >
                <Text
                  style={[styles.optionTitle, { color: palette.textPrimary }]}
                >
                  {option.label}
                </Text>
                {option.subtitle ? (
                  <Text
                    style={[
                      styles.optionMeta,
                      { color: palette.textSecondary },
                    ]}
                  >
                    {option.subtitle}
                  </Text>
                ) : null}
              </Pressable>
            ))
          )}
        </View>
      ) : null}

      {error ? (
        <Text
          accessibilityRole="alert"
          style={[styles.error, { color: palette.error }]}
        >
          {error}
        </Text>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { gap: spacing.sm },
  label: {
    ...typography.caption,
    fontWeight: '600',
  },
  selectedRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    minWidth: 0,
  },
  dropdownButton: {
    flex: 1,
    minWidth: 0,
    minHeight: 48,
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: spacing.md,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  dropdownLabel: {
    ...typography.body,
    flex: 1,
    minWidth: 0,
  },
  disabled: { opacity: 0.6 },
  panel: {
    borderWidth: 1,
    borderRadius: 10,
    padding: spacing.md,
    gap: spacing.sm,
  },
  option: {
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
  },
  optionTitle: { ...typography.body },
  optionMeta: { ...typography.caption },
  help: { ...typography.caption },
  error: { ...typography.caption },
});
